import { BarChart3, Mail, MessageSquare, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ChannelUsageStats } from "../../hooks/use-notification-settings";

interface NotificationUsageCardProps {
  usage: {
    EMAIL: ChannelUsageStats;
    SMS: ChannelUsageStats;
    WHATSAPP: ChannelUsageStats;
    resetDate: string;
  };
  isLoading?: boolean;
  className?: string;
}

interface ChannelUsageItemProps {
  icon: React.ReactNode;
  label: string;
  stats: ChannelUsageStats;
  color: "green" | "yellow" | "red";
}

function ChannelUsageItem({ icon, label, stats, color }: ChannelUsageItemProps) {
  const getColorClasses = (color: "green" | "yellow" | "red") => {
    switch (color) {
      case "green":
        return "bg-green-500";
      case "yellow":
        return "bg-yellow-500";
      case "red":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getBadgeVariant = (color: "green" | "yellow" | "red") => {
    switch (color) {
      case "green":
        return "default" as const;
      case "yellow":
        return "secondary" as const;
      case "red":
        return "destructive" as const;
      default:
        return "default" as const;
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm">{label}</span>
        </div>
        <Badge variant={getBadgeVariant(color)}>{stats.usagePercentage}%</Badge>
      </div>

      <div className="space-y-2">
        <Progress
          value={stats.usagePercentage}
          className="h-2"
          indicatorClassName={getColorClasses(color)}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{stats.currentUsage.toLocaleString()} used</span>
          <span>
            {stats.isUnlimited ? "Unlimited" : `${stats.monthlyLimit.toLocaleString()} limit`}
          </span>
        </div>
      </div>
    </div>
  );
}

export function NotificationUsageCard({ usage, isLoading, className }: NotificationUsageCardProps) {
  const { t } = useTranslation("settings");

  const getUsageColor = (percentage: number): "green" | "yellow" | "red" => {
    if (percentage >= 90) return "red";
    if (percentage >= 70) return "yellow";
    return "green";
  };

  const formatResetDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <SettingCard
        title={t("notifications.usage.title", { defaultValue: "Usage & Limits" })}
        icon={<BarChart3 className="h-5 w-5" />}
        className={className}
      >
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-2 bg-muted rounded animate-pulse" />
              <div className="flex justify-between">
                <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </SettingCard>
    );
  }

  return (
    <SettingCard
      title={t("notifications.usage.title", { defaultValue: "Usage & Limits" })}
      icon={<BarChart3 className="h-5 w-5" />}
      className={className}
      helpText={t("notifications.usage.description", {
        defaultValue: "Current usage and monthly limits for channels. Resets on {resetDate}.",
        resetDate: formatResetDate(usage.resetDate),
      })}
    >
      <div className="space-y-6">
        <ChannelUsageItem
          icon={<Mail className="h-4 w-4 text-blue-500" />}
          label={t("notifications.channels.email", { defaultValue: "Email" })}
          stats={usage.EMAIL}
          color={getUsageColor(usage.EMAIL.usagePercentage)}
        />

        <ChannelUsageItem
          icon={<MessageSquare className="h-4 w-4 text-green-500" />}
          label={t("notifications.channels.whatsapp", { defaultValue: "WhatsApp" })}
          stats={usage.WHATSAPP}
          color={getUsageColor(usage.WHATSAPP.usagePercentage)}
        />

        <ChannelUsageItem
          icon={<Phone className="h-4 w-4 text-orange-500" />}
          label={t("notifications.channels.sms", { defaultValue: "SMS" })}
          stats={usage.SMS}
          color={getUsageColor(usage.SMS.usagePercentage)}
        />
      </div>
    </SettingCard>
  );
}
